import {
  ArrowRight,
  BriefcaseBusiness,
  Check,
  CircleUserRound,
  ContactRound,
  FileCheck2,
  FileText,
  GraduationCap,
  LockKeyhole,
  MessageSquareText,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  Target,
  UserRoundCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardMockup } from '@/components/marketing/dashboard-mockup';
import { Badge, buttonVariants } from '@/components/ui';
import { routes } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

const journey = [
  { key: 'profile', icon: CircleUserRound },
  { key: 'cv', icon: FileText },
  { key: 'match', icon: Target },
  { key: 'prepare', icon: MessageSquareText },
] as const;

const features = [
  { key: 'cvBuilder', icon: FileText, href: routes.cvFeature, tone: 'blue' },
  { key: 'cvCheck', icon: FileCheck2, href: routes.cvFeature, tone: 'ink' },
  { key: 'linkedin', icon: ContactRound, href: routes.linkedinFeature, tone: 'cyan' },
  { key: 'match', icon: BriefcaseBusiness, href: routes.register, tone: 'green' },
  { key: 'interview', icon: MessageSquareText, href: routes.interviewFeature, tone: 'amber' },
] as const;

const audiences = [
  { key: 'student', icon: GraduationCap },
  { key: 'graduate', icon: UserRoundCheck },
  { key: 'professional', icon: BriefcaseBusiness },
  { key: 'switcher', icon: RefreshCcw },
] as const;

export function LandingPage() {
  const { t } = useTranslation(['landing', 'common']);
  const path = t('landing:journey.path', { returnObjects: true }) as string[];
  usePageMeta({
    title: t('common:seo.homeTitle'),
    description: t('common:seo.homeDescription'),
  });

  return (
    <>
      <section className="hero-section section-pad">
        <div className="hero-glow hero-glow--one" aria-hidden="true" />
        <div className="hero-glow hero-glow--two" aria-hidden="true" />
        <div className="container hero-grid">
          <div className="hero-copy">
            <Badge tone="brand">
              <Sparkles size={14} /> {t('landing:hero.eyebrow')}
            </Badge>
            <h1>{t('landing:hero.title')}</h1>
            <p className="hero-copy__subtitle">{t('landing:hero.subtitle')}</p>
            <div className="hero-actions">
              <Link className={buttonVariants({ size: 'lg' })} to={routes.register}>
                {t('common:actions.buildCv')} <ArrowRight className="rtl-flip" size={18} />
              </Link>
              <Link
                className={buttonVariants({ variant: 'secondary', size: 'lg' })}
                to={routes.linkedinFeature}
              >
                {t('common:actions.checkProfile')}
              </Link>
            </div>
            <p className="hero-note">
              <Check size={16} /> {t('landing:hero.note')}
            </p>
          </div>
          <div className="hero-visual">
            <DashboardMockup />
          </div>
        </div>
        <div className="container journey-strip" aria-label={t('landing:journey.ariaLabel')}>
          {path.map((step, index) => (
            <div key={step}>
              <span>{index + 1}</span>
              <strong>{step}</strong>
              {index < 4 && <ArrowRight className="rtl-flip" size={15} />}
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad journey-section">
        <div className="container">
          <div className="section-heading section-heading--center">
            <p className="eyebrow">{t('landing:journey.eyebrow')}</p>
            <h2>{t('landing:journey.title')}</h2>
            <p>{t('landing:journey.subtitle')}</p>
          </div>
          <div className="journey-grid">
            {journey.map(({ key, icon: Icon }, index) => (
              <article className="journey-card" key={key}>
                <div className="journey-card__top">
                  <span>0{index + 1}</span>
                  <div>
                    <Icon size={21} />
                  </div>
                </div>
                <h3>{t(`landing:journey.steps.${key}.title`)}</h3>
                <p>{t(`landing:journey.steps.${key}.body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad feature-section">
        <div className="container">
          <div className="section-heading">
            <p className="eyebrow">{t('landing:features.eyebrow')}</p>
            <h2>{t('landing:features.title')}</h2>
            <p>{t('landing:features.subtitle')}</p>
          </div>
          <div className="feature-grid">
            {features.map(({ key, icon: Icon, href, tone }, index) => (
              <Link
                className={`feature-card feature-card--${tone} ${index < 2 ? 'feature-card--wide' : ''}`}
                to={href}
                key={key}
              >
                <div className="feature-card__top">
                  <div className="feature-card__icon">
                    <Icon size={22} />
                  </div>
                  <ArrowRight className="rtl-flip" size={19} />
                </div>
                <div>
                  <Badge>{t(`landing:features.${key}.tag`)}</Badge>
                  <h3>{t(`landing:features.${key}.title`)}</h3>
                  <p>{t(`landing:features.${key}.body`)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad audience-section">
        <div className="container audience-layout">
          <div className="section-heading">
            <p className="eyebrow">{t('landing:audience.eyebrow')}</p>
            <h2>{t('landing:audience.title')}</h2>
          </div>
          <div className="audience-grid">
            {audiences.map(({ key, icon: Icon }) => (
              <article className="audience-card" key={key}>
                <div>
                  <Icon size={20} />
                </div>
                <h3>{t(`landing:audience.${key}.title`)}</h3>
                <p>{t(`landing:audience.${key}.body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="principles-section section-pad">
        <div className="container principles-card">
          <div className="principles-card__icon">
            <ShieldCheck size={30} />
          </div>
          <div className="principles-card__copy">
            <h2>{t('landing:principles.title')}</h2>
            <p>{t('landing:principles.body')}</p>
          </div>
          <ul>
            <li>
              <LockKeyhole size={17} /> {t('landing:principles.private')}
            </li>
            <li>
              <UserRoundCheck size={17} /> {t('landing:principles.grounded')}
            </li>
            <li>
              <Sparkles size={17} /> {t('landing:principles.bilingual')}
            </li>
          </ul>
        </div>
      </section>

      <section className="section-pad final-cta-section">
        <div className="container final-cta">
          <div>
            <p className="eyebrow">{t('landing:final.eyebrow')}</p>
            <h2>{t('landing:final.title')}</h2>
            <p>{t('landing:final.body')}</p>
          </div>
          <Link
            className={buttonVariants({ variant: 'secondary', size: 'lg' })}
            to={routes.register}
          >
            {t('common:actions.startFree')} <ArrowRight className="rtl-flip" size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}

export default LandingPage;
